import { Kitchen } from '../../../libs';
import bahrainPlan from '../plans/bahrain.json';
import kuwaitBasementPlan from '../plans/kuwait-basement.json';
import kuwaitFirstPlan from '../plans/kuwait-first.json';
import saudiPlan from '../plans/saudi.json';
import uaePlan from '../plans/uae.json';
import { FloorPlan, FloorPoint, FloorZone, KitchenCamera, KitchenCameraListing } from '../types';
import { zoneForStation } from './stations';
import { hasKitchenCameras } from './vssQueries';

/**
 * The plans, and `withCameras`, which attaches the cameras VSS lists to one.
 *
 * One JSON per kitchen under `plans/`, all in the same schema and all at ten
 * plan units to the metre, the scale `tower/scene/dimensions.ts` converts by.
 *
 * `bahrain.json` is in the points of `GROUND_FLOOR_LAYOUT.pdf` (AutoCAD, A3), at
 * that scale. Its walls are the centrelines of `walls_only.svg`, one per wall,
 * curves flattened; `solid` is empty. Zones were ray-cast from each room's label
 * to the nearest wall, then checked and re-cut against these walls.
 *
 * Every other file carries one centreline per wall and nothing else: all that
 * is left on those sheets is dimension chains and fixtures, so their `detail`
 * and `solid` are empty. `saudi.json` is Riyadh (MODON Third Industrial City,
 * factories 39/40/41 and 56/57), `uae.json` Al Quoz Dubai (warehouses 4 and 5),
 * and the two `kuwait-*.json` the two floors of one building. Room names are
 * drawn as outlines rather than text on all but the Kuwait first floor, so
 * those zone names were read off the sheet by hand and the rectangles cast
 * against the extracted walls.
 *
 * Each plan is in its own frame — switching floor refits the canvas rather than
 * holding one building still — because nothing on the Kuwait sheets ties the
 * first floor's fitted block to a point on the basement.
 */

/** One floor of one kitchen. Kitchens with a single floor still have one. */
export interface KitchenLevel {
  id: string;
  name: string;
}

interface LevelSource extends KitchenLevel {
  plan: FloorPlan;
}

const asPlan = (raw: unknown): FloorPlan => raw as FloorPlan;

const LEVELS_BY_KITCHEN: Partial<Record<Kitchen, LevelSource[]>> = {
  [Kitchen.BH001]: [{ id: 'ground', name: 'Ground floor', plan: asPlan(bahrainPlan) }],
  // SA001 is taken to be the Riyadh kitchen; SA002 has no plan here yet.
  [Kitchen.SA001]: [{ id: 'ground', name: 'Ground floor', plan: asPlan(saudiPlan) }],
  [Kitchen.AE001]: [{ id: 'ground', name: 'Ground floor', plan: asPlan(uaePlan) }],
  [Kitchen.KW001]: [
    { id: 'basement', name: 'Basement', plan: asPlan(kuwaitBasementPlan) },
    // The eastern half of this floor is unfitted shell on the sheet supplied, so
    // this is the fitted block only — not the whole floor plate.
    { id: 'first', name: 'First floor', plan: asPlan(kuwaitFirstPlan) }
  ]
};

/** A plan, its zones and its cameras, with the lookups the view reads them through. */
export interface KitchenPlan {
  kitchen: Kitchen;
  level: KitchenLevel;
  plan: FloorPlan;
  zones: FloorZone[];
  zoneById: Map<string, FloorZone>;
  /** Empty until `withCameras` attaches the cameras VSS lists. */
  cameras: KitchenCamera[];
  cameraById: Map<string, KitchenCamera>;
  /** VSS has cameras in this kitchen, so the live feed runs here. */
  camerasEnabled: boolean;
  /** Every zone's distinct camera rooms; a zone's headcount sums these (never its cameras). */
  roomsByZone: Map<string, string[]>;
  /** Every distinct camera room mapped to a zone here, for the room-occupancy batch query. */
  rooms: string[];
  zoneName: (id: string | null) => string;
  zoneAt: (point: FloorPoint) => FloorZone | null;
}

/** A camera with no room, or one no zone matches, stays in the list with a null `zoneId`. */
const attachCameras = (zoneById: Map<string, FloorZone>, listed: readonly KitchenCameraListing[]) => {
  const cameras: KitchenCamera[] = listed.map(({ id, room }) => ({
    id,
    room,
    zoneId: room ? zoneForStation(zoneById, room) : null
  }));

  const roomsByZone = new Map<string, string[]>();
  for (const { room, zoneId } of cameras) {
    if (!room || !zoneId) {
      continue;
    }
    const rooms = roomsByZone.get(zoneId) ?? [];
    if (!rooms.includes(room)) {
      rooms.push(room);
    }
    roomsByZone.set(zoneId, rooms);
  }

  return {
    cameras,
    cameraById: new Map(cameras.map((camera) => [camera.id, camera])),
    roomsByZone,
    rooms: [...new Set([...roomsByZone.values()].flat())]
  };
};

const build = (kitchen: Kitchen, level: LevelSource): KitchenPlan => {
  const { plan } = level;
  const zones = plan.zones;
  const zoneById = new Map(zones.map((zone) => [zone.id, zone]));

  return {
    kitchen,
    level: { id: level.id, name: level.name },
    plan,
    zones,
    zoneById,
    ...attachCameras(zoneById, []),
    camerasEnabled: hasKitchenCameras(kitchen),
    zoneName: (id) => (id ? (zoneById.get(id)?.name ?? id) : 'Off camera'),
    zoneAt: (point) => zones.find((zone) => zoneContains(zone, point)) ?? null
  };
};

/** `plan` with the cameras VSS lists attached, each to the zone whose room it names. */
export const withCameras = (plan: KitchenPlan, listed: readonly KitchenCameraListing[]): KitchenPlan => ({
  ...plan,
  ...attachCameras(plan.zoneById, listed)
});

const CACHE = new Map<string, KitchenPlan>();

/** The floors drawn for a kitchen, in the order they are stacked. Empty if none. */
export const levelsFor = (kitchen: Kitchen): KitchenLevel[] =>
  (LEVELS_BY_KITCHEN[kitchen] ?? []).map(({ id, name }) => ({ id, name }));

/** One floor of a kitchen, defaulting to the lowest drawn; null where none is. */
export const planFor = (kitchen: Kitchen, levelId?: string): KitchenPlan | null => {
  const levels = LEVELS_BY_KITCHEN[kitchen];
  if (!levels?.length) {
    return null;
  }
  const level = (levelId && levels.find((entry) => entry.id === levelId)) || levels[0];
  const key = `${kitchen}:${level.id}`;
  const cached = CACHE.get(key);
  if (cached) {
    return cached;
  }
  const built = build(kitchen, level);
  CACHE.set(key, built);
  return built;
};

/** Even-odd ray cast against the zone's ring. */
export const zoneContains = (zone: FloorZone, point: FloorPoint): boolean => {
  const ring = zone.polygon;
  let inside = false;
  for (let i = 0, j = ring.length - 2; i < ring.length; j = i, i += 2) {
    const xi = ring[i];
    const yi = ring[i + 1];
    const xj = ring[j];
    const yj = ring[j + 1];
    if (yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};
